import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { NightsRange } from './types';
import {
  MAX_NIGHTS,
  MIN_NIGHTS,
  normalizeRange,
  setFrom,
  setTo,
} from '../lib/nightsRange';
import { formatNightsRange } from '../lib/formatNightsLabel';
import { NIGHTS_RANGE_TEXTS } from '../config/texts';

export interface UseNightsRange {
  from: Ref<number>;
  to: Ref<number>;
  controlActivated: Ref<boolean>;

  selection: ComputedRef<NightsRange>;
  valueLabel: ComputedRef<string>;
  summaryLabel: ComputedRef<string>;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setFromValue: (v: number) => void;
  setToValue: (v: number) => void;
  restore: (range: NightsRange | null) => void;
  validate: () => boolean;
}

export interface UseNightsRangeOptions {
  initial?: NightsRange | null;
  checkInLabel?: string | (() => string | undefined);
}

export function useNightsRange(options: UseNightsRangeOptions = {}): UseNightsRange {
  const initialRange = normalizeRange(options.initial ?? null);
  const getCheckInLabel = (): string => {
    const v = typeof options.checkInLabel === 'function' ? options.checkInLabel() : options.checkInLabel;
    return v ?? '';
  };

  const from = ref(initialRange.from);
  const to = ref(initialRange.to);
  const controlActivated = ref(false);

  const selection = computed<NightsRange>(() => ({ from: from.value, to: to.value }));

  const valueLabel = computed<string>(
    () => NIGHTS_RANGE_TEXTS.valuePrefix + formatNightsRange(from.value, to.value, '-'),
  );

  const summaryLabel = computed<string>(
    () =>
      NIGHTS_RANGE_TEXTS.summaryPrefix +
      getCheckInLabel() +
      NIGHTS_RANGE_TEXTS.summaryMid +
      formatNightsRange(from.value, to.value, '–'),
  );

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setFromValue = (v: number): void => {
    const next = setFrom(selection.value, v);
    from.value = next.from;
    to.value = next.to;
  };

  const setToValue = (v: number): void => {
    const next = setTo(selection.value, v);
    from.value = next.from;
    to.value = next.to;
  };

  const restore = (range: NightsRange | null): void => {
    const next = normalizeRange(range);
    from.value = next.from;
    to.value = next.to;
    controlActivated.value = false;
  };

  const validate = (): boolean =>
    from.value >= MIN_NIGHTS && from.value <= to.value && to.value <= MAX_NIGHTS;

  return {
    from,
    to,
    controlActivated,
    selection,
    valueLabel,
    summaryLabel,
    open,
    close,
    toggle,
    setFromValue,
    setToValue,
    restore,
    validate,
  };
}
